/**
 * Resolved, typed environment for the SPA. Only VITE_* vars are exposed to the
 * browser by Vite.
 */
/**
 * API base URL. When VITE_API_BASE_URL is unset/empty (the unified single-
 * container deploy, where the API serves the SPA same-origin), fall back to the
 * current page origin so requests stay relative to wherever the app is served.
 */
const rawApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const resolvedApiBaseUrl =
  rawApiBaseUrl && rawApiBaseUrl.length > 0
    ? rawApiBaseUrl
    : typeof window !== 'undefined'
      ? window.location.origin
      : 'http://localhost:3001';

/**
 * Identity settings handed over at RUNTIME by the API's `/config.js`, which index.html loads
 * before this bundle.
 *
 * Why it exists: `import.meta.env.VITE_*` is substituted by Vite at BUILD time, so the tenant,
 * client id and scope were baked into the image. The API has always read its own copy from the
 * environment, so the server could be pointed at any tenant — the browser could not. A second
 * install on a second identity surface would have needed a second image built from the same
 * source, and a rebuild for every customer after that.
 *
 * The build-time values remain the fallback, so `vite dev` and any install that does not serve
 * `/config.js` behave exactly as before.
 */
const runtime: Partial<
  Record<
    | 'entraTenantId'
    | 'entraSpaClientId'
    | 'entraApiScope'
    | 'entraAuthority'
    | 'customerDirectory'
    | 'googleClientId',
    string
  >
> =
  (typeof window !== 'undefined' && window.__MG_CONFIG__) || {};

const entraTenantId = runtime.entraTenantId || import.meta.env.VITE_ENTRA_TENANT_ID || '';

export const env = {
  apiBaseUrl: resolvedApiBaseUrl.replace(/\/$/, ''),
  entraTenantId,
  entraSpaClientId: runtime.entraSpaClientId || import.meta.env.VITE_ENTRA_SPA_CLIENT_ID || '',
  entraApiScope: runtime.entraApiScope || import.meta.env.VITE_ENTRA_API_SCOPE || '',
  /**
   * Where sign-in happens. Derived from the tenant for a workforce tenant, which is every install
   * that existed before this; an External ID (CIAM) tenant serves sign-in from `<tenant>.ciamlogin.com`
   * and must send its own authority through `/config.js`.
   */
  entraAuthority:
    runtime.entraAuthority || `https://login.microsoftonline.com/${entraTenantId}`,
  /**
   * Which sign-in to offer. Runtime-only, with no build-time fallback: an install that does not
   * serve `/config.js` has no Google configuration either, and defaulting to Microsoft is what
   * every install did before Google existed.
   *
   * The API sends 'google' ONLY when Google sign-in would actually work, so this never advertises
   * a button that opens a consent screen ending in an error.
   */
  customerDirectory: runtime.customerDirectory === 'google' ? ('google' as const) : ('microsoft' as const),
  /** Non-secret, and empty unless the API judged Google sign-in usable. */
  googleClientId: runtime.googleClientId || '',
};

/**
 * When true, the app skips interactive MSAL login and trusts the API's DEV_AUTH
 * seeded principal (GET /api/auth/me). This is the case in `vite dev`, or when
 * VITE_DEV_AUTH=true, or when no Entra SPA client id is configured (no app
 * registration exists yet).
 */
export const devAuthEnabled: boolean =
  import.meta.env.DEV ||
  import.meta.env.VITE_DEV_AUTH === 'true' ||
  env.entraSpaClientId.length === 0;
